import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Search, Plus, Phone, Mail, Building2, MapPin, Edit2, Trash2,
  User, Tag, Filter, X, ExternalLink, GitMerge, AlertTriangle, Check, ChevronRight,
  ChevronDown, CheckSquare, Square, MoreHorizontal, FolderEdit, FileEdit,
  Loader2, Briefcase, Download, RefreshCw, Globe, Clock,
  Users as UsersIcon, UserCircle2, FolderOpen, Activity, Maximize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import HelpIcon from '@/components/HelpIcon';
import ContactDetailDrawer from '@/components/ContactDetailDrawer';
import RelationshipStatusBadge, { STATUS_META } from '@/components/RelationshipStatusBadge';
import WarningGateDialog from '@/components/WarningGateDialog';
import GroupPicker from '@/components/GroupPicker';
import ContactDialog from '@/components/ContactDialog';
import GoogleImportDialog from '@/components/GoogleImportDialog';
import ContactSyncSettings from '@/components/ContactSyncSettings';
import useContactForm from '@/hooks/useContactForm';
import { Lock } from 'lucide-react';
import RulebookShortcut from '@/components/rulebook/RulebookShortcut';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getCurrentUserId } from '../utils/currentUser';
import '@/styles/bid-board.css';
import '@/styles/site-office.css';
import { kindLabel, kindOf } from '@/utils/partyKind';

// DP273-P2b — the kinds the firm asks paperwork of; only these rows can have
// a standing to read.
const STANDING_KINDS = ['gc', 'sub', 'interior_designer', 'supplier'];
// The two named views are a reading of Contacts, not separate pages.
const VIEW_KINDS = { contractors: ['gc', 'sub', 'interior_designer'], vendors: ['supplier'] };
// Name | Kind & trade | Groups | Standing/Terms | Primary phone | Actions
const ROW_GRID = 'grid-cols-[minmax(0,2fr)_minmax(0,1.6fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_120px]';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CATEGORIES = [
  "Client",
  "General Contractor",
  "Subcontractor - Electrical",
  "Subcontractor - Plumbing",
  "Subcontractor - HVAC",
  "Subcontractor - Carpentry",
  "Subcontractor - Flooring",
  "Subcontractor - Tile",
  "Subcontractor - Painting",
  "Subcontractor - Roofing",
  "Subcontractor - Other",
  "Vendor/Supplier",
  "Inspector/Official",
  "Business General",
  "Personal",
  "Other"
];

// Helper: which category group does a category belong to?
export function getCategoryGroup(category) {
  if (!category) return 'general';
  const lower = category.toLowerCase();
  if (lower === 'client') return 'client';
  if (lower.includes('contractor') || lower.includes('subcontractor')) return 'contractor';
  if (lower.includes('vendor') || lower.includes('supplier')) return 'vendor';
  if (lower.includes('inspector') || lower.includes('official')) return 'inspector';
  return 'general'; // Personal, Business General, Other
}

// Color treatment for category-group badge in the dense list.
// The chip TEXT shows the contact's actual category (e.g. "Subcontractor -
// Plumbing"). The color treatment is still derived from the group bucket so
// all subcontractor categories share the amber tone, vendors share emerald,
// etc — that's a visual grouping aid, not a label.
const CATEGORY_GROUP_BADGE = {
  client:     'bg-blue-50 text-blue-700 border-blue-200',
  contractor: 'bg-amber-50 text-amber-700 border-amber-200',
  vendor:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  inspector:  'bg-purple-50 text-purple-700 border-purple-200',
  general:    'bg-stone-50 text-stone-600 border-stone-200',
};

// Render the contact's actual category, lightly shortened for the dense
// row. Strips the "Subcontractor - " prefix because every row in that
// bucket would otherwise repeat it; the amber color already signals
// "subcontractor" visually.
function formatRoleChipText(category) {
  if (!category) return 'Other';
  const trimmed = category.trim();
  if (trimmed.toLowerCase().startsWith('subcontractor - ')) {
    return trimmed.slice('Subcontractor - '.length);
  }
  return trimmed;
}

// Format a date string as "Mar 12" / "2024" relative-ish for the last-activity chip
function formatActivityDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return 'Today';
    if (diffDays < 2) return 'Yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : '2-digit' });
  } catch { return null; }
}

// Deduplicate phone numbers for display
function getUniquePhones(contact) {
  const phones = [];
  const seen = new Set();
  const addPhone = (val, label) => {
    if (!val) return;
    const normalized = val.replace(/[\s\-\(\)\.]/g, '');
    if (seen.has(normalized)) return;
    seen.add(normalized);
    phones.push({ value: val, label });
  };
  addPhone(contact.phone, 'Phone');
  addPhone(contact.phone_mobile, 'Mobile');
  addPhone(contact.phone_work, 'Work');
  return phones;
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Feb 2026 — honor `?needs_completion=1` deep-link from the My Desk nudge
  // so the user lands directly on the filtered list. Only applied on mount.
  // After that, the filter is fully user-controlled via the toggle pill.
  const _initialNeedsCompletion = searchParams.get('needs_completion') === '1';
  const [contacts, setContacts] = useState([]);
  // Phase 3 — Contact Hub detail drawer
  const [detailContact, setDetailContact] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  // DP273-P2b — the page reads by Kind. `view` is a named reading of the same
  // list, opened from the menu; the older Contractors and Vendors pages stay.
  const [selectedKind, setSelectedKind] = useState('all');
  const [kinds, setKinds] = useState([]);
  const [standing, setStanding] = useState({});
  const [kindCounts, setKindCounts] = useState({});
  const view = ['contractors', 'vendors'].includes(searchParams.get('view'))
    ? searchParams.get('view')
    : 'contacts';
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all'); // 'all' | 'tenant' | 'personal'
  const [needsCompletionOnly, setNeedsCompletionOnly] = useState(_initialNeedsCompletion);

  // Groups lookup — small in-memory cache so the dense row can render
  // each contact's linked group name without N round-trips.
  const [groupsById, setGroupsById] = useState({}); // {group_id: {id,name,type,...}}
  const [groupsAll, setGroupsAll] = useState([]);

  // Dense list — inline expand state + lazy-loaded row details (projects + last activity)
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [rowDetails, setRowDetails] = useState({}); // { [contactId]: { projects, last_activity_at, loading } }

  // Dialog state — moved to useContactForm hook (Feb 2026 refactor).
  const [specCategories, setSpecCategories] = useState([]);

  // Duplicate/Merge state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [currentMergeGroup, setCurrentMergeGroup] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [merging, setMerging] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);

  // Bulk selection state
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);
  // 2026-02 bulk tag management
  const [showBulkAddTagDialog, setShowBulkAddTagDialog] = useState(false);
  const [showBulkRemoveTagDialog, setShowBulkRemoveTagDialog] = useState(false);
  const [bulkTagValue, setBulkTagValue] = useState('');
  const [bulkTagCustom, setBulkTagCustom] = useState('');
  const [bulkCategoryCustom, setBulkCategoryCustom] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [processingBulk, setProcessingBulk] = useState(false);
  // 2026-02-17 (iter327) — permanent-delete confirmation. Lives only
  // in the Archive view. Typed-word ("DELETE") gate.
  const [permDeleteState, setPermDeleteState] = useState({
    open: false, ids: [], typed: '',
  });

  // Google Contacts state
  const [showGoogleImport, setShowGoogleImport] = useState(false);
  // 2026-02 Archive (soft-delete) support
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
  const [archivedList, setArchivedList] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [undoState, setUndoState] = useState(null); // { ids: [...], count }
  const [googleContacts, setGoogleContacts] = useState([]);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [selectedGoogleContacts, setSelectedGoogleContacts] = useState(new Set());
  const [importingGoogle, setImportingGoogle] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);

  // Add-to-RFP dialog state (Feature B)
  const [showRfpDialog, setShowRfpDialog] = useState(false);
  const [rfpProjects, setRfpProjects] = useState([]);
  const [rfpProjectId, setRfpProjectId] = useState('');
  const [rfpDraftList, setRfpDraftList] = useState([]);
  const [rfpTargetId, setRfpTargetId] = useState(''); // existing rfp id or "__new__"
  const [rfpNewTitle, setRfpNewTitle] = useState('');
  const [rfpSubmitting, setRfpSubmitting] = useState(false);

  // Warning-gate dialog (fires when selection contains warning-status contacts)
  const [warningGate, setWarningGate] = useState({ open: false, contacts: [], action: null, label: '' });

  useEffect(() => {
    fetchContacts();
    fetchTags();
    checkForDuplicates();
    fetchSpecCategories();
    checkGoogleStatus();
    fetchGroups();
    fetchKinds();
  }, [selectedCategory, selectedTag, selectedTier, needsCompletionOnly, selectedKind, view]);

  // Iter 291 — pull-now on ContactsPage open (2s debounce), then a
  // 15-min light poll while the tab stays open. Fire-and-forget; the
  // dialog surfaces any errors via its sync-log viewer.
  useEffect(() => {
    let mounted = true;
    const firePull = () => {
      axios.post(`${API}/contact-sync/pull-now`).catch(() => {});
    };
    const t = setTimeout(() => { if (mounted) firePull(); }, 2000);
    const poll = setInterval(() => { if (mounted) firePull(); }, 15 * 60 * 1000);
    return () => { mounted = false; clearTimeout(t); clearInterval(poll); };
  }, []);

  // Load the tenant's contact-groups so each row can render the linked
  // group's name + icon. Refetched on mount and after group changes.
  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/contact-groups`);
      const list = res.data?.groups || [];
      setGroupsAll(list);
      const map = {};
      for (const g of list) map[g.id] = g;
      setGroupsById(map);
    } catch { /* non-fatal */ }
  };

  const checkGoogleStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/google/status?user_id=${getCurrentUserId()}`);
      setGoogleConnected(res.data.connected);
    } catch { /* ignore */ }
  };

  const fetchSpecCategories = async () => {
    try {
      const res = await axios.get(`${API}/spec-categories`);
      setSpecCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching spec categories:', error);
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      const n = new Set(prev);
      n.has(contactId) ? n.delete(contactId) : n.add(contactId);
      return n;
    });
  };

  const selectAllContacts = () => setSelectedContacts(new Set(contacts.map(c => c.id)));
  const clearSelection = () => { setSelectedContacts(new Set()); setIsSelectionMode(false); };

  // 2026-02-17 (iter327) — the main-list permanent Delete has been
  // removed. Bulk removal on the active list goes through
  // handleBulkArchive (soft-delete). Permanent delete lives ONLY in
  // the Archive view — see handlePermDelete* below.

  const handleBulkCategoryChange = async () => {
    if (selectedContacts.size === 0 || !bulkCategory) return;
    // Warning gate first
    const warnings = contacts.filter(c => selectedContacts.has(c.id) && (c.relationship_status === 'warning'));
    if (warnings.length > 0) {
      setWarningGate({
        open: true,
        contacts: warnings,
        label: 'change category',
        action: () => { setWarningGate(g => ({ ...g, open: false })); void _doBulkCategoryChange(); },
      });
      return;
    }
    return _doBulkCategoryChange();
  };

  const _doBulkCategoryChange = async () => {
    const chosen = (bulkCategoryCustom || bulkCategory || '').trim();
    if (!chosen) return;
    if (!window.confirm(`Set category to '${chosen}' for ${selectedContacts.size} contact${selectedContacts.size === 1 ? '' : 's'}?`)) return;
    setProcessingBulk(true);
    try {
      const res = await axios.post(`${API}/contacts/bulk-action`, {
        contact_ids: Array.from(selectedContacts), action: 'update_category', category: chosen
      });
      // 2026-02-17 (iter327) — Bulk Undo toast (~10s). The server
      // returned each contact's PRIOR category in `previous_state`;
      // Undo posts it back via action=restore_categories, which is
      // tenant-scoped and only touches the `category` field.
      const prev = res.data?.previous_state || {};
      const modified = res.data?.modified_count ?? 0;
      toast.success(`${modified} contact${modified === 1 ? '' : 's'} recategorised`, {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const r2 = await axios.post(`${API}/contacts/bulk-action`, {
                action: 'restore_categories',
                restore_map: prev,
              });
              const n = r2.data?.modified_count ?? 0;
              toast.success(`Restored previous category on ${n} contact${n === 1 ? '' : 's'}`);
              fetchContacts();
            } catch { toast.error('Undo failed'); }
          },
        },
      });
      clearSelection(); setShowBulkCategoryDialog(false); setBulkCategory(''); setBulkCategoryCustom(''); fetchContacts();
    } catch { toast.error('Failed to update contacts'); }
    finally { setProcessingBulk(false); }
  };

  // 2026-02 bulk Add Tag / Remove Tag — server-side is atomic
  // ($addToSet / $pull), tenant-scoped, only modifies `tags`.
  const _doBulkAddTag = async () => {
    const tag = (bulkTagCustom || bulkTagValue || '').trim();
    if (!tag) return;
    if (!window.confirm(`Add tag '${tag}' to ${selectedContacts.size} contact${selectedContacts.size === 1 ? '' : 's'}?`)) return;
    // Snapshot the target ids BEFORE clearSelection() nukes them —
    // Undo needs them to post remove_tag on the same set.
    const targetIds = Array.from(selectedContacts);
    setProcessingBulk(true);
    try {
      const res = await axios.post(`${API}/contacts/bulk-action`, {
        contact_ids: targetIds, action: 'add_tag', tag,
      });
      const modified = res.data?.modified_count ?? 0;
      toast.success(`Tag '${tag}' added to ${modified} contact${modified === 1 ? '' : 's'}`, {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const r2 = await axios.post(`${API}/contacts/bulk-action`, {
                contact_ids: targetIds, action: 'remove_tag', tag,
              });
              const n = r2.data?.modified_count ?? 0;
              toast.success(`Tag '${tag}' removed from ${n} contact${n === 1 ? '' : 's'}`);
              fetchContacts();
            } catch { toast.error('Undo failed'); }
          },
        },
      });
      clearSelection(); setShowBulkAddTagDialog(false); setBulkTagValue(''); setBulkTagCustom(''); fetchContacts();
    } catch { toast.error('Failed to add tag'); }
    finally { setProcessingBulk(false); }
  };

  const _doBulkRemoveTag = async () => {
    const tag = (bulkTagValue || '').trim();
    if (!tag) return;
    if (!window.confirm(`Remove tag '${tag}' from ${selectedContacts.size} contact${selectedContacts.size === 1 ? '' : 's'}?`)) return;
    const targetIds = Array.from(selectedContacts);
    setProcessingBulk(true);
    try {
      const res = await axios.post(`${API}/contacts/bulk-action`, {
        contact_ids: targetIds, action: 'remove_tag', tag,
      });
      const modified = res.data?.modified_count ?? 0;
      toast.success(`Tag '${tag}' removed from ${modified} contact${modified === 1 ? '' : 's'}`, {
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const r2 = await axios.post(`${API}/contacts/bulk-action`, {
                contact_ids: targetIds, action: 'add_tag', tag,
              });
              const n = r2.data?.modified_count ?? 0;
              toast.success(`Tag '${tag}' added back to ${n} contact${n === 1 ? '' : 's'}`);
              fetchContacts();
            } catch { toast.error('Undo failed'); }
          },
        },
      });
      clearSelection(); setShowBulkRemoveTagDialog(false); setBulkTagValue(''); fetchContacts();
    } catch { toast.error('Failed to remove tag'); }
    finally { setProcessingBulk(false); }
  };

  const checkForDuplicates = async () => {
    try {
      const res = await axios.get(`${API}/contacts/duplicates/find`);
      setDuplicateGroups(res.data.duplicate_groups || []);
      setDuplicateCount(res.data.total_duplicates || 0);
    } catch { /* ignore */ }
  };

  const openMergeDialog = (group = null) => {
    if (group) { setCurrentMergeGroup(group); setSelectedPrimary(group.contacts[0]?.id); }
    else if (duplicateGroups.length > 0) { setCurrentMergeGroup(duplicateGroups[0]); setSelectedPrimary(duplicateGroups[0].contacts[0]?.id); }
    setShowMergeDialog(true);
  };

  const handleMerge = async () => {
    if (!currentMergeGroup || !selectedPrimary) return;
    const mergeIds = currentMergeGroup.contacts.filter(c => c.id !== selectedPrimary).map(c => c.id);
    if (mergeIds.length === 0) { toast.error('Select contacts to merge'); return; }
    setMerging(true);
    try {
      await axios.post(`${API}/contacts/merge`, { primary_id: selectedPrimary, merge_ids: mergeIds });
      toast.success(`Merged ${mergeIds.length} duplicate(s)`);
      const currentIndex = duplicateGroups.findIndex(g => g === currentMergeGroup);
      const remaining = duplicateGroups.filter((_, i) => i !== currentIndex);
      setDuplicateGroups(remaining);
      if (remaining.length > 0) { setCurrentMergeGroup(remaining[0]); setSelectedPrimary(remaining[0].contacts[0]?.id); }
      else { setShowMergeDialog(false); setCurrentMergeGroup(null); }
      fetchContacts(); checkForDuplicates();
    } catch { toast.error('Failed to merge contacts'); }
    finally { setMerging(false); }
  };

  const skipMergeGroup = () => {
    const idx = duplicateGroups.findIndex(g => g === currentMergeGroup);
    if (idx < duplicateGroups.length - 1) { setCurrentMergeGroup(duplicateGroups[idx + 1]); setSelectedPrimary(duplicateGroups[idx + 1].contacts[0]?.id); }
    else setShowMergeDialog(false);
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedTag !== 'all') params.tag = selectedTag;
      const response = await axios.get(`${API}/contacts/list`, { params });
      const rows = Array.isArray(response.data) ? response.data : (response.data?.contacts || []);

      // DP273-P2b — the chips count every row this fetch returned, read by
      // Kind. A row saved before the Kind existed is read off its old
      // category word (kindOf), so the counts and the list agree whether or
      // not migration 0086 has been applied yet.
      const counts = { all: rows.length };
      for (const c of rows) {
        const key = kindOf(c) || 'need';
        counts[key] = (counts[key] || 0) + 1;
      }
      setKindCounts(counts);

      // The Kind is the filter; a named view fixes the kinds it reads.
      const viewKinds = VIEW_KINDS[view];
      let data = rows;
      if (viewKinds) {
        const pick = viewKinds.includes(selectedKind) ? [selectedKind] : viewKinds;
        data = rows.filter(c => pick.includes(kindOf(c)));
      } else if (selectedKind === 'need') {
        data = rows.filter(c => !kindOf(c));
      } else if (selectedKind !== 'all') {
        data = rows.filter(c => kindOf(c) === selectedKind);
      }
      if (selectedTier !== 'all') {
        data = data.filter(c => (c.ownership_tier || 'tenant') === selectedTier);
      }
      if (needsCompletionOnly) {
        data = data.filter(c => c.needs_completion === true);
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        data = data.filter(c =>
          c.first_name?.toLowerCase().includes(q) || c.last_name?.toLowerCase().includes(q) ||
          c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q) || c.phone_mobile?.toLowerCase().includes(q) ||
          c.phone_work?.toLowerCase().includes(q)
        );
      }
      setContacts(data);
      fetchStanding(data);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  };

  // DP273-P2b — where each contractor-kind row stands with the firm. One call
  // per 200 rows; a failure leaves the column empty rather than the page.
  const fetchStanding = async (rows) => {
    const ids = rows.filter(c => STANDING_KINDS.includes(kindOf(c))).map(c => c.id);
    if (ids.length === 0) return;
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      try {
        const r = await axios.get(`${API}/contacts/standing`, { params: { ids: chunk.join(',') } });
        const answered = r.data || {};
        // A row the server does not answer for is asked nothing at all — mark
        // it so the cell stops saying "reading…".
        const merged = {};
        chunk.forEach(id => { merged[id] = answered[id] || false; });
        setStanding(prev => ({ ...prev, ...merged }));
      } catch { /* the column simply reads nothing */ }
    }
  };

  // The chip counts: every row once, read by kind.
  const fetchKinds = async () => {
    try {
      const r = await axios.get(`${API}/contacts/kinds`);
      setKinds(r.data?.kinds || []);
    } catch { /* the chip row falls back to what the rows carry */ }
  };

  // 2026-02 Archive (soft-delete) helpers — always tenant-scoped by the
  // backend, which validates ids against current_user.company_id before
  // touching anything. Nothing is ever hard-deleted here.
  const fetchArchived = async () => {
    try {
      setArchivedLoading(true);
      const r = await axios.get(`${API}/contacts/archived`);
      setArchivedList(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error('Failed to load archived contacts'); }
    finally { setArchivedLoading(false); }
  };

  const handleSelectAllMatching = async () => {
    try {
      const body = {};
      if (selectedCategory !== 'all') body.category = selectedCategory;
      if (selectedTag !== 'all') body.tag = selectedTag;
      const r = await axios.post(`${API}/contacts/select-all-matching`, body);
      const ids = (r.data?.ids || []);
      setSelectedContacts(new Set(ids));
      toast.success(`Selected ${ids.length} contact${ids.length === 1 ? '' : 's'} matching the current filter`);
    } catch { toast.error('Select-all failed'); }
  };

  const handleBulkArchive = async () => {
    if (selectedContacts.size === 0) return;
    const ids = Array.from(selectedContacts);
    if (!window.confirm(`Archive ${ids.length} contact${ids.length === 1 ? '' : 's'}?`)) return;
    try {
      setProcessingBulk(true);
      const r = await axios.post(`${API}/contacts/bulk/archive`, { contact_ids: ids });
      const n = r.data?.archived_count ?? 0;
      toast.success(`${n} contact${n === 1 ? '' : 's'} archived`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(`${API}/contacts/bulk/restore`, { contact_ids: ids });
              toast.success(`${n} contact${n === 1 ? '' : 's'} restored`);
              fetchContacts();
              if (viewMode === 'archived') fetchArchived();
            } catch { toast.error('Restore failed'); }
          },
        },
      });
      setUndoState({ ids, count: n });
      clearSelection();
      fetchContacts();
    } catch { toast.error('Archive failed'); }
    finally { setProcessingBulk(false); }
  };

  const handleRestoreOne = async (contactId) => {
    try {
      const r = await axios.post(`${API}/contacts/bulk/restore`, { contact_ids: [contactId] });
      const n = r.data?.restored_count ?? 0;
      toast.success(`${n} contact restored`);
      fetchArchived();
    } catch { toast.error('Restore failed'); }
  };

  const handleBulkRestore = async () => {
    const ids = archivedList.filter(c => selectedContacts.has(c.id)).map(c => c.id);
    if (ids.length === 0) return;
    try {
      const r = await axios.post(`${API}/contacts/bulk/restore`, { contact_ids: ids });
      const n = r.data?.restored_count ?? 0;
      toast.success(`${n} contact${n === 1 ? '' : 's'} restored`);
      clearSelection();
      fetchArchived();
    } catch { toast.error('Restore failed'); }
  };

  // 2026-02-17 (iter327) — permanent delete lives ONLY in the Archive
  // view. Both entry points (per-row + bulk-selected) open the same
  // typed-word AlertDialog before calling POST /contacts/bulk/delete-permanent.
  const openPermDeleteFor = (ids) => {
    if (!ids || ids.length === 0) return;
    setPermDeleteState({ open: true, ids: [...ids], typed: '' });
  };
  const openPermDeleteOne = (id) => openPermDeleteFor([id]);
  const openPermDeleteBulk = () => {
    const ids = archivedList.filter(c => selectedContacts.has(c.id)).map(c => c.id);
    openPermDeleteFor(ids);
  };
  const handlePermDeleteConfirm = async () => {
    if (permDeleteState.typed !== 'DELETE') return;
    setProcessingBulk(true);
    try {
      const r = await axios.post(`${API}/contacts/bulk/delete-permanent`, {
        contact_ids: permDeleteState.ids,
        confirm: 'DELETE',
      });
      const n = r.data?.deleted_count ?? 0;
      const rejected = (r.data?.rejected_ids || []).length;
      toast.success(
        `${n} contact${n === 1 ? '' : 's'} permanently deleted${rejected > 0 ? ` (${rejected} rejected)` : ''}`
      );
      setPermDeleteState({ open: false, ids: [], typed: '' });
      clearSelection();
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Permanent delete failed');
    } finally { setProcessingBulk(false); }
  };

  // Lazy load linked-projects count + last-activity timestamp for an expanded row.
  // Cached in rowDetails so re-expanding a row is instant.
  const fetchRowDetails = async (contactId) => {
    if (rowDetails[contactId]?.loaded) return;
    setRowDetails(prev => ({ ...prev, [contactId]: { ...(prev[contactId] || {}), loading: true } }));
    try {
      const [projRes, actRes] = await Promise.all([
        axios.get(`${API}/contacts/${contactId}/projects`).catch(() => ({ data: { projects: [] } })),
        axios.get(`${API}/contacts/${contactId}/activities`).catch(() => ({ data: { activities: [] } })),
      ]);
      const projects = projRes.data?.projects || [];
      const activities = actRes.data?.activities || [];
      const last = activities[0]?.occurred_at || activities[0]?.created_at || null;
      setRowDetails(prev => ({
        ...prev,
        [contactId]: {
          loaded: true, loading: false,
          projects_count: projects.length,
          last_activity_at: last,
        },
      }));
    } catch {
      setRowDetails(prev => ({ ...prev, [contactId]: { loaded: true, loading: false, projects_count: 0, last_activity_at: null } }));
    }
  };

  const toggleExpand = (contactId) => {
    setExpandedIds(prev => {
      const n = new Set(prev);
      if (n.has(contactId)) {
        n.delete(contactId);
      } else {
        n.add(contactId);
        // fire-and-forget lazy fetch
        fetchRowDetails(contactId);
      }
      return n;
    });
  };

  const fetchTags = async () => {
    try { const r = await axios.get(`${API}/contacts/tags`); setAllTags(r.data.tags); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchContacts(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Feb 2026 refactor — Add/Edit Contact dialog state + handlers extracted
  // into a dedicated hook. ContactsPage drives openAddDialog/openEditDialog
  // and reads form.formData.category for the dynamic-fields catGroup.
  const form = useContactForm({ API, fetchContacts, fetchTags, fetchGroups });
  const { openAddDialog, openEditDialog } = form;

  const handleDelete = async (contactId) => {
    // 2026-02-17 (iter327) — per-row Delete on the active list is now
    // Archive (soft-delete). Permanent delete only exists in the
    // Archive view, behind the typed-word AlertDialog.
    if (!window.confirm('Archive this contact? You can restore it from the Archive view.')) return;
    try {
      await axios.post(`${API}/contacts/bulk/archive`, { contact_ids: [contactId] });
      toast.success('Contact archived');
      fetchContacts();
    } catch { toast.error('Failed to archive contact'); }
  };

  const clearFilters = () => { setSearchQuery(''); setSelectedCategory('all'); setSelectedKind('all'); setSelectedTag('all'); setSelectedTier('all'); setNeedsCompletionOnly(false); };

  // --- Google Contacts ---
  const fetchGoogleContacts = async () => {
    setLoadingGoogle(true);
    try {
      const res = await axios.get(`${API}/google-contacts/list?user_id=${getCurrentUserId()}&page_size=500`);
      setGoogleContacts(res.data.contacts || []);
      setSelectedGoogleContacts(new Set());
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to fetch Google contacts';
      toast.error(msg);
    } finally { setLoadingGoogle(false); }
  };

  const openGoogleImportDialog = () => {
    // Iter 286 — the new GoogleImportDialog does its own phase management
    // (/status → connect or preview → apply). We just open it here.
    setShowGoogleImport(true);
  };

  const toggleGoogleContact = (idx) => {
    setSelectedGoogleContacts(prev => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  const selectAllGoogle = () => {
    setSelectedGoogleContacts(new Set(googleContacts.map((_, i) => i)));
  };

  const importSelectedGoogleContacts = async () => {
    if (selectedGoogleContacts.size === 0) { toast.error('Select contacts to import'); return; }
    setImportingGoogle(true);
    try {
      const selected = Array.from(selectedGoogleContacts).map(i => googleContacts[i]);
      const res = await axios.post(`${API}/google-contacts/import?user_id=${getCurrentUserId()}`, { contacts: selected });
      toast.success(res.data.message);
      setShowGoogleImport(false);
      fetchContacts();
    } catch { toast.error('Failed to import contacts'); }
    finally { setImportingGoogle(false); }
  };

  // --- Add to RFP (Feature B) ---
  // Pattern: project → pick existing draft RFP OR create a new RFP, then call
  // the bulk add-contractors endpoint. Reuses the existing RFP machinery —
  // POST /api/projects/{pid}/contractor-rfps for creation, POST /api/contractor-
  // rfps/{rid}/add-contractors for the bulk recipient add.
  const openAddToRfpDialog = async () => {
    if (selectedContacts.size === 0) { toast.error('Select contacts first'); return; }
    // Warning gate first — list flagged contacts before we even open the picker.
    const warnings = contacts.filter(c => selectedContacts.has(c.id) && c.relationship_status === 'warning');
    if (warnings.length > 0) {
      setWarningGate({
        open: true,
        contacts: warnings,
        label: 'continue to RFP picker',
        action: () => { setWarningGate(g => ({ ...g, open: false })); void _openRfpPicker(); },
      });
      return;
    }
    return _openRfpPicker();
  };

  const _openRfpPicker = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      const list = Array.isArray(res.data) ? res.data : (res.data?.projects || []);
      // Lightweight project list — keep only id + name
      const sanitized = list.map(p => ({ id: p.id, name: p.name || p.title || p.project_name || 'Untitled project' }));
      setRfpProjects(sanitized);
      setRfpProjectId('');
      setRfpDraftList([]);
      setRfpTargetId('');
      setRfpNewTitle('');
      setShowRfpDialog(true);
    } catch {
      toast.error('Failed to load projects');
    }
  };

  // When the user picks a project, fetch its draft RFPs so they can either
  // pick an existing one or create a new one.
  useEffect(() => {
    if (!rfpProjectId) { setRfpDraftList([]); setRfpTargetId(''); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/contractor-rfps?project_id=${rfpProjectId}&status=draft`);
        const drafts = res.data?.contractor_rfps || [];
        setRfpDraftList(drafts);
        setRfpTargetId(drafts.length > 0 ? drafts[0].id : '__new__');
      } catch {
        setRfpDraftList([]);
        setRfpTargetId('__new__');
      }
    })();
  }, [rfpProjectId]);

  const submitAddToRfp = async () => {
    if (!rfpProjectId) { toast.error('Pick a project'); return; }
    if (!rfpTargetId) { toast.error('Pick an RFP or create new'); return; }
    setRfpSubmitting(true);
    try {
      let rfpId = rfpTargetId;
      if (rfpTargetId === '__new__') {
        // Derive a trade from the currently-filtered category, if any.
        const trade = selectedCategory !== 'all' ? selectedCategory : undefined;
        const title = rfpNewTitle.trim() || (trade ? `RFP for ${trade}` : 'RFP');
        const createRes = await axios.post(`${API}/projects/${rfpProjectId}/contractor-rfps`, {
          title, trade, rfp_type: 'general',
        });
        rfpId = createRes.data.id;
      }
      const ids = Array.from(selectedContacts);
      const addRes = await axios.post(`${API}/contractor-rfps/${rfpId}/add-contractors`, {
        contractor_ids: ids,
      });
      const added = addRes.data.added?.length || 0;
      const skipped = addRes.data.skipped_already_present?.length || 0;
      const notFound = addRes.data.not_found?.length || 0;
      toast.success(
        `Added ${added} to RFP` +
        (skipped > 0 ? ` · ${skipped} already on RFP` : '') +
        (notFound > 0 ? ` · ${notFound} not found` : '')
      );
      setShowRfpDialog(false);
      clearSelection();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to RFP');
    } finally {
      setRfpSubmitting(false);
    }
  };

  // DP273-P2b — grouped by Kind, in the firm's own order, then the two
  // standing kinds, then the rows nobody has sorted yet.
  const kindOrder = [
    ...kinds.map(k => k.key).filter(k => k !== 'personal' && k !== 'other'),
    'personal', 'other', 'need',
  ];
  const groupedContacts = contacts.reduce((acc, contact) => {
    const key = kindOf(contact) || 'need';
    if (!acc[key]) acc[key] = [];
    acc[key].push(contact);
    return acc;
  }, {});
  const groupedEntries = Object.entries(groupedContacts).sort(
    (a, b) => (kindOrder.indexOf(a[0]) + 1 || 99) - (kindOrder.indexOf(b[0]) + 1 || 99),
  );

  // The chips this view offers: all of them on Contacts, the three contractor
  // kinds in the Contractors view, Suppliers in the Vendors view.
  const visibleKinds = VIEW_KINDS[view]
    ? kinds.filter(k => VIEW_KINDS[view].includes(k.key))
    : kinds;

  // The Standing column is a contractor reading; the Vendors view reads Terms.
  const fourthColumn = view === 'vendors' ? 'Terms' : 'Standing';

  const standingText = (row) => {
    if (!row) return '';
    const worst = [...(row.documents || [])].sort(
      (a, b) => ['ok', 'due', 'missing', 'overdue'].indexOf(b.state)
        - ['ok', 'due', 'missing', 'overdue'].indexOf(a.state),
    )[0];
    let text;
    if (row.status === 'none') text = 'nothing asked yet';
    else if (row.status === 'ok') text = 'on file';
    else text = `${worst?.label || 'Paperwork'} ${row.status}${worst?.due_date ? ` ${worst.due_date}` : ''}`;
    const signed = row.agreement?.signed_projects || 0;
    if (signed > 0) text += ` · agreement on ${signed} project${signed === 1 ? '' : 's'}`;
    return text;
  };

  const standingClass = (row) => {
    if (!row || row.status === 'none') return 'kso-tag';
    if (row.status === 'ok') return 'kso-tag ok';
    if (row.status === 'due') return 'kso-tag wait';
    return 'kso-tag late';
  };

  const catGroup = getCategoryGroup(form.formData.category);

  return (
    <div className="min-h-screen bg-background">
      {/* Selection Bar */}
      {selectedContacts.size > 0 && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-primary text-primary-foreground shadow-lg">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={clearSelection} className="text-primary-foreground hover:bg-primary-foreground/20">
                  <X className="w-4 h-4 mr-2" />Cancel
                </Button>
                <span className="font-medium">{selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={selectAllContacts} className="text-primary-foreground hover:bg-primary-foreground/20">
                  <CheckSquare className="w-4 h-4 mr-2" />Select All ({contacts.length})
                </Button>
                {viewMode === 'active' && (
                  <Button variant="ghost" size="sm" onClick={handleSelectAllMatching} className="text-primary-foreground hover:bg-primary-foreground/20" data-testid="bulk-select-all-matching-btn" title="Select every contact matching the current filter, even beyond the visible page">
                    <CheckSquare className="w-4 h-4 mr-2" />Select all matching filter
                  </Button>
                )}
                <div className="h-4 w-px bg-primary-foreground/30" />
                {viewMode === 'active' ? (
                  <>
                    <Button variant="ghost" size="sm" onClick={openAddToRfpDialog} className="text-primary-foreground hover:bg-primary-foreground/20" data-testid="bulk-add-to-rfp-btn">
                      <FileEdit className="w-4 h-4 mr-2" />Add to RFP
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowBulkCategoryDialog(true)} className="text-primary-foreground hover:bg-primary-foreground/20" data-testid="bulk-change-category-btn">
                      <FolderEdit className="w-4 h-4 mr-2" />Change Category
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setBulkTagValue(''); setBulkTagCustom(''); setShowBulkAddTagDialog(true); }} className="text-primary-foreground hover:bg-primary-foreground/20" data-testid="bulk-add-tag-btn">
                      <FolderEdit className="w-4 h-4 mr-2" />Add Tag
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setBulkTagValue(''); setShowBulkRemoveTagDialog(true); }} className="text-primary-foreground hover:bg-primary-foreground/20" data-testid="bulk-remove-tag-btn">
                      <FolderEdit className="w-4 h-4 mr-2" />Remove Tag
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleBulkArchive} disabled={processingBulk} className="text-amber-100 hover:bg-amber-500/20" data-testid="bulk-archive-btn">
                      <Trash2 className="w-4 h-4 mr-2" />Archive selected
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" onClick={handleBulkRestore} disabled={processingBulk} className="text-emerald-100 hover:bg-emerald-500/20" data-testid="bulk-restore-btn">
                      <CheckSquare className="w-4 h-4 mr-2" />Restore selected
                    </Button>
                    <Button variant="ghost" size="sm" onClick={openPermDeleteBulk} disabled={processingBulk} className="text-red-200 hover:bg-red-500/20 hover:text-red-100" data-testid="bulk-delete-permanent-btn">
                      <Trash2 className="w-4 h-4 mr-2" />Delete permanently
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2026-02 Archive view — soft-deleted contacts, per-row Restore */}
      {viewMode === 'archived' && (
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-6" data-testid="contacts-archived-view">
          <div className="mb-3 text-sm text-muted-foreground">
            Archived contacts are hidden from the main list and fully restorable. Use <span className="font-medium text-red-600">Delete permanently</span> here (per-row or bulk) if you want to hard-delete a contact — that action is tenant-scoped and cannot be undone.
          </div>
          {archivedLoading ? (
            <div className="text-sm text-muted-foreground py-8">Loading…</div>
          ) : archivedList.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8" data-testid="contacts-archived-empty">No archived contacts.</div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm" data-testid="contacts-archived-table">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="p-2 w-8">
                      <input
                        type="checkbox"
                        checked={archivedList.length > 0 && archivedList.every(c => selectedContacts.has(c.id))}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedContacts(new Set(archivedList.map(c => c.id)));
                          else clearSelection();
                        }}
                        data-testid="contacts-archived-select-all"
                      />
                    </th>
                    <th className="p-2">Name</th>
                    <th className="p-2">Company</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Archived at</th>
                    <th className="p-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedList.map((c) => (
                    <tr key={c.id} className="border-t" data-testid={`archived-row-${c.id}`}>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedContacts.has(c.id)}
                          onChange={(e) => {
                            const next = new Set(selectedContacts);
                            if (e.target.checked) next.add(c.id);
                            else next.delete(c.id);
                            setSelectedContacts(next);
                          }}
                          data-testid={`archived-checkbox-${c.id}`}
                        />
                      </td>
                      <td className="p-2">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '(unnamed)'}</td>
                      <td className="p-2">{c.company || '—'}</td>
                      <td className="p-2">{c.category || '—'}</td>
                      <td className="p-2 font-mono text-xs">{c.archived_at || '—'}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => handleRestoreOne(c.id)} data-testid={`archived-restore-${c.id}`}>
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openPermDeleteOne(c.id)}
                          className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          data-testid={`archived-delete-permanent-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />Delete permanently
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50" style={viewMode === 'archived' ? { display: 'none' } : undefined}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2 relative">
                <HelpIcon elementId="nav-contacts" position="inline" size="md" />
                <div>
                  <h1 className="font-heading font-light text-3xl tracking-tight flex items-center gap-2">
                    {view === 'contractors' ? 'Contractors' : view === 'vendors' ? 'Vendors' : 'Contacts'}
                    <RulebookShortcut nodeId="connections.contacts" />
                  </h1>
                  {view === 'contacts' ? (
                    <p className="text-sm text-muted-foreground mt-1">{contacts.length} contacts</p>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1" data-testid="contacts-view-note">
                      {contacts.length} {view === 'contractors' ? 'contractors' : 'suppliers'} · a view of Contacts
                      {' · '}
                      <button
                        type="button"
                        className="underline"
                        onClick={() => (view === 'contractors' ? navigate('/contractors') : navigate('/vendors'))}
                        data-testid="contacts-open-older-page"
                      >
                        Open the older {view === 'contractors' ? 'Contractors' : 'Vendors'} page
                      </button>
                    </p>
                  )}
                </div>
                <BlueprintIndicator sectionId="contacts-overview" tooltip="Learn about Contacts" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-md border overflow-hidden" data-testid="contacts-view-toggle">
                <button
                  type="button"
                  onClick={() => { setViewMode('active'); clearSelection(); }}
                  className={`px-3 py-1.5 text-xs ${viewMode === 'active' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-accent'}`}
                  data-testid="contacts-view-active-btn"
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => { setViewMode('archived'); clearSelection(); fetchArchived(); }}
                  className={`px-3 py-1.5 text-xs ${viewMode === 'archived' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-accent'}`}
                  data-testid="contacts-view-archived-btn"
                >
                  Archive ({archivedList.length})
                </button>
              </div>
              <Button variant={isSelectionMode ? "secondary" : "outline"} size="sm"
                onClick={() => { setIsSelectionMode(!isSelectionMode); if (isSelectionMode) clearSelection(); }}
                data-testid="toggle-selection-mode-btn">
                {isSelectionMode ? (<><X className="w-4 h-4 mr-2" />Exit Select</>) : (<><CheckSquare className="w-4 h-4 mr-2" />Select</>)}
              </Button>
              {/*
                2026-02 SAFETY REMOVAL — the "Merge Duplicates ({duplicateCount})"
                button was hidden from the header after investigation:
                  * The endpoint it consumed (GET /api/contacts/duplicates/find)
                    groups contacts by shared email alone OR shared last name
                    alone, producing false-positive "duplicates" of clearly
                    different people (e.g. Doug Spradlin + Natalie Spradlin).
                  * The merge path it triggered (POST /api/contacts/merge)
                    HARD-DELETES the loser rows with no undo / no snapshot.
                We are intentionally NOT deleting `duplicateCount`,
                `openMergeDialog`, `showMergeDialog`, or the `/duplicates/find`
                and `/merge` endpoints — a safer rebuild will use the existing
                dedupe review-queue path (routes/contacts_dedupe.py) with
                snapshots. This is a UI-only removal.
              {duplicateCount > 0 && (
                <Button variant="outline" onClick={() => openMergeDialog()} className="border-amber-500 text-amber-600 hover:bg-amber-50" data-testid="merge-duplicates-btn">
                  <GitMerge className="w-4 h-4 mr-2" />Merge Duplicates ({duplicateCount})
                </Button>
              )}
              */}
              {/* Iter 286 — button always visible; the dialog handles the
                  connect/preview/apply phases internally, so even
                  unconnected users can click here to start the OAuth flow. */}
              <Button variant="outline" size="sm" onClick={openGoogleImportDialog} data-testid="google-import-btn">
                <Globe className="w-4 h-4 mr-2" />Import Google
              </Button>
              <Button onClick={() => openAddDialog()} data-testid="add-contact-btn">
                <Plus className="w-4 h-4 mr-2" />Add Contact
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="border-b border-border bg-card">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search name, email, phone, company…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-contacts-input" />
              </div>
            </div>
            <div className="md:col-span-3">
              <Select value={selectedTier} onValueChange={setSelectedTier}>
                <SelectTrigger data-testid="filter-tier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="tenant">Firm</SelectItem>
                  <SelectItem value="personal">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger data-testid="filter-tag-select"><SelectValue placeholder="All Tags" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map(tag => <SelectItem key={tag} value={tag}>{tag}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center justify-end">
              {(selectedKind !== 'all' || selectedTag !== 'all' || selectedTier !== 'all' || searchQuery) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full" data-testid="clear-filters-btn"><X className="w-4 h-4 mr-1" />Clear</Button>
              )}
            </div>
          </div>

          {/* DP273-P2b — the kind chips. The one reading the whole page uses. */}
          <div className="kbb-steps mt-3" data-testid="contacts-kinds">
            <button
              type="button"
              className="kbb-step"
              aria-pressed={selectedKind === 'all'}
              onClick={() => setSelectedKind('all')}
              data-testid="contacts-kind-all"
            >
              All <span className="n">{visibleKinds.length
                ? visibleKinds.reduce((n, k) => n + (kindCounts[k.key] || 0), 0)
                : (kindCounts.all || 0)}</span>
            </button>
            {visibleKinds.map(k => (
              <button
                key={k.key}
                type="button"
                className="kbb-step"
                aria-pressed={selectedKind === k.key}
                onClick={() => setSelectedKind(k.key)}
                data-testid={`contacts-kind-${k.key}`}
              >
                {k.label} <span className="n">{kindCounts[k.key] || 0}</span>
              </button>
            ))}
            {view === 'contacts' && (
              <button
                type="button"
                className="kbb-step"
                aria-pressed={selectedKind === 'need'}
                onClick={() => setSelectedKind('need')}
                data-testid="contacts-kind-need"
              >
                Needs a kind <span className="n">{kindCounts.need || 0}</span>
              </button>
            )}
          </div>
          {!loading && (
            <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-muted-foreground" data-testid="contacts-result-count">
              <span>
                Showing <span className="font-medium text-foreground">{contacts.length}</span> {contacts.length === 1 ? 'contact' : 'contacts'}
                {(selectedKind !== 'all' || selectedTag !== 'all' || selectedTier !== 'all' || needsCompletionOnly || searchQuery) ? ' (filtered)' : ''}
              </span>
              {/* "Needs completing" toggle pill — surfaces incomplete contacts
                  fed by the auto-clearing needs_completion flag. */}
              <button
                type="button"
                onClick={() => setNeedsCompletionOnly(v => !v)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border transition-colors text-[11px] ${
                  needsCompletionOnly
                    ? 'bg-amber-100 text-amber-800 border-amber-300'
                    : 'bg-transparent text-muted-foreground border-border hover:bg-muted'
                }`}
                data-testid="filter-needs-completion-pill"
              >
                {needsCompletionOnly ? '✓ ' : ''}Needs completing
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contacts List */}
      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        {loading ? (
          <div className="text-center py-24"><p className="text-muted-foreground">Loading contacts...</p></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-20 h-20 border border-border mb-6"><User className="w-10 h-10 text-muted-foreground" /></div>
            <h2 className="font-heading text-2xl mb-2">No contacts found</h2>
            <p className="text-muted-foreground mb-6">Add your first contact to get started</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => openAddDialog()}><Plus className="w-4 h-4 mr-2" />Add Contact</Button>
              <Button variant="outline" onClick={openGoogleImportDialog}><Globe className="w-4 h-4 mr-2" />Import from Google</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedEntries.map(([kindKey, categoryContacts]) => (
              <section key={kindKey} data-testid={`contacts-group-${kindKey}`}>
                {/* Kind section header — minimal, dense */}
                <div className="flex items-center gap-2 mb-2 px-2">
                  <h2 className="font-heading text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    {kindKey === 'need' ? 'Needs a kind' : kindLabel(kindKey, kinds)}
                  </h2>
                  <span className="text-xs text-muted-foreground">· {categoryContacts.length}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Dense list — rows */}
                <div className="border border-border bg-card divide-y divide-border" data-testid={`contacts-list-${kindKey}`}>
                  {/* Desktop column headers (sm and up).
                      Columns: Name | Kind & trade | Groups | Standing/Terms | Primary phone | Actions */}
                  <div className={`hidden md:grid ${ROW_GRID} gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30`}>
                    <div>Name</div>
                    <div>Role</div>
                    <div>Groups</div>
                    <div>{fourthColumn}</div>
                    <div>Primary phone</div>
                    <div className="text-right">Actions</div>
                  </div>

                  {categoryContacts.map(contact => {
                    const isSelected = selectedContacts.has(contact.id);
                    const isExpanded = expandedIds.has(contact.id);
                    const contactName = contact.first_name
                      ? `${contact.first_name} ${contact.last_name || ''}`.trim()
                      : contact.name || 'Unknown';
                    const phones = getUniquePhones(contact);
                    const primaryPhone = phones[0]?.value || '';
                    const tier = contact.ownership_tier || 'tenant';
                    const rowKind = kindOf(contact);
                    const rowTrade = contact.trades?.[0] || contact.trade || '';
                    const specs = contact.spec_categories || [];
                    const rowStanding = standing[contact.id];
                    const details = rowDetails[contact.id];
                    const lastActivity = details?.last_activity_at;

                    return (
                      <div
                        key={contact.id}
                        data-testid={`contact-row-${contact.id}`}
                        className={`group transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
                      >
                        {/* Collapsed row — clickable to inline-expand */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (isSelectionMode) {
                              toggleContactSelection(contact.id);
                            } else {
                              toggleExpand(contact.id);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (isSelectionMode) toggleContactSelection(contact.id);
                              else toggleExpand(contact.id);
                            }
                          }}
                          className="cursor-pointer min-h-[44px] md:min-h-0"
                        >
                          {/* DESKTOP / TABLET ROW — table-like grid
                              Columns: Name(+tier icon) | Role | Company/Family group | Primary phone | Actions */}
                          <div className={`hidden md:grid ${ROW_GRID} gap-3 items-center px-3 py-2 text-sm`}>
                            {/* Name + checkbox + expand chevron + status + tier icon */}
                            <div className="flex items-center gap-2 min-w-0">
                              {(isSelectionMode || selectedContacts.size > 0) && (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleContactSelection(contact.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`contact-checkbox-${contact.id}`}
                                />
                              )}
                              {isExpanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                              <RelationshipStatusBadge
                                contact={contact}
                                onChange={(updated) => {
                                  setContacts(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
                                }}
                              />
                              <span
                                className="shrink-0"
                                title={tier === 'personal' ? 'Private — visible only to you' : 'Firm — shared with the company'}
                                data-testid={`contact-tier-icon-${contact.id}`}
                              >
                                {tier === 'personal'
                                  ? <Lock className="w-3 h-3 text-indigo-500" />
                                  : <UsersIcon className="w-3 h-3 text-slate-400" />}
                              </span>
                              <span className="font-medium truncate">{contactName}</span>
                              {contact.needs_completion && (
                                <Badge variant="outline" className="text-[9px] h-4 px-1 bg-amber-50 text-amber-700 border-amber-200 shrink-0" title="This contact is missing details. Click to complete.">
                                  Needs completing
                                </Badge>
                              )}
                            </div>

                            {/* DP273-P2b — the kind, the trade, and what they
                                price: the row says what this party IS. */}
                            <div className="min-w-0 flex flex-wrap items-center gap-1">
                              <span
                                className={`kso-tag${rowKind === 'gc' ? ' you' : rowKind ? '' : ' wait'}`}
                                title={contact.category || ''}
                                data-testid="contact-kind-chip"
                              >
                                {kindLabel(rowKind, kinds)}
                              </span>
                              {rowTrade && (
                                <span className="kso-tag" data-testid="contact-trade-chip">{rowTrade}</span>
                              )}
                              {specs.slice(0, 3).map(prefix => (
                                <span key={prefix} className="kso-tag" title="Spec Category" data-testid={`contact-spec-chip-${prefix}`}>{prefix}</span>
                              ))}
                              {specs.length > 3 && (
                                <span className="kso-tag" title="Spec Categories">+{specs.length - 3}</span>
                              )}
                            </div>

                            {/* GROUPS column — multi-group chips (Feb 2026).
                                Falls back to legacy single group_id when
                                a contact hasn't been migrated yet. */}
                            <div className="text-muted-foreground truncate flex items-center gap-1 flex-wrap">
                              {(() => {
                                const gids = Array.isArray(contact.group_ids) && contact.group_ids.length > 0
                                  ? contact.group_ids
                                  : (contact.group_id ? [contact.group_id] : []);
                                if (gids.length === 0) {
                                  return (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openEditDialog(contact); }}
                                      className="text-muted-foreground/70 hover:text-foreground hover:underline text-xs"
                                      data-testid={`contact-group-assign-${contact.id}`}
                                      title="Click to assign a group"
                                    >
                                      — Assign…
                                    </button>
                                  );
                                }
                                return gids.map((gid, idx) => {
                                  const g = groupsById[gid];
                                  if (!g) return null;
                                  const isCompany = g.type === 'company';
                                  const isOrg = g.type === 'organization';
                                  const Icon = isCompany ? Building2 : isOrg ? Globe : UsersIcon;
                                  const tone = isCompany ? 'text-blue-600'
                                    : isOrg ? 'text-amber-700' : 'text-indigo-600';
                                  return (
                                    <span
                                      key={gid}
                                      className="inline-flex items-center gap-1 max-w-[180px]"
                                      data-testid={`contact-group-chip-${contact.id}-${idx}`}
                                      title={`${g.name} (${g.type})${idx === 0 ? ' — primary' : ''}`}
                                    >
                                      <Icon className={`w-3 h-3 shrink-0 ${tone}`} />
                                      <span className="truncate text-foreground text-xs">{g.name}</span>
                                    </span>
                                  );
                                });
                              })()}
                            </div>

                            {/* DP273-P2b — Standing (contractor kinds) or the
                                vendor's Terms, by view. */}
                            {view === 'vendors' ? (
                              <div className="min-w-0 truncate">
                                <span className="kct-m" data-testid="contact-terms">{contact.payment_terms || '—'}</span>
                              </div>
                            ) : (
                              <div className="min-w-0 truncate">
                                {STANDING_KINDS.includes(rowKind) ? (
                                  rowStanding === undefined ? (
                                    <span className="kso-tag" data-testid="contact-standing">reading…</span>
                                  ) : (
                                    <span className={standingClass(rowStanding || undefined)} data-testid="contact-standing">
                                      {rowStanding ? standingText(rowStanding) : 'nothing asked yet'}
                                    </span>
                                  )
                                ) : <span className="text-muted-foreground/40">—</span>}
                              </div>
                            )}

                            {/* Primary phone */}
                            <div className="text-muted-foreground truncate flex items-center gap-1.5">
                              {primaryPhone ? (
                                <>
                                  <Phone className="w-3 h-3 shrink-0" />
                                  <span className="truncate">{primaryPhone}</span>
                                </>
                              ) : <span className="text-muted-foreground/40">—</span>}
                            </div>

                            {/* Quick actions */}
                            <div className="flex items-center justify-end gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                              {primaryPhone && (
                                <a
                                  href={`tel:${primaryPhone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1.5 hover:bg-muted rounded"
                                  title={`Call ${primaryPhone}`}
                                  data-testid={`contact-call-${contact.id}`}
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                </a>
                              )}
                              {contact.email && (
                                <a
                                  href={`mailto:${contact.email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1.5 hover:bg-muted rounded"
                                  title={`Email ${contact.email}`}
                                  data-testid={`contact-mail-${contact.id}`}
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                </a>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); setDetailContact(contact); setDetailOpen(true); }}
                                className="p-1.5 hover:bg-muted rounded"
                                title="Open full details"
                                data-testid={`contact-details-${contact.id}`}
                              >
                                <Maximize2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditDialog(contact); }}
                                className="p-1.5 hover:bg-muted rounded"
                                title="Edit"
                                data-testid={`edit-contact-${contact.id}`}
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* MOBILE ROW — name + one primary action */}
                          <div className="md:hidden flex items-center gap-2 px-3 py-3">
                            {(isSelectionMode || selectedContacts.size > 0) && (
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleContactSelection(contact.id)}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`contact-checkbox-mobile-${contact.id}`}
                              />
                            )}
                            {isExpanded
                              ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                              : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <RelationshipStatusBadge
                                  contact={contact}
                                  onChange={(updated) => {
                                    setContacts(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
                                  }}
                                />
                                <span className="font-medium text-sm truncate">{contactName}</span>
                                {tier === 'personal'
                                  ? <Lock className="w-3.5 h-3.5 text-indigo-500 shrink-0" title="Private — visible only to you" />
                                  : <UsersIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" title="Firm — shared" />}
                                {contact.needs_completion && (
                                  <Badge variant="outline" className="text-[9px] h-4 px-1 bg-amber-50 text-amber-700 border-amber-200 shrink-0">
                                    !
                                  </Badge>
                                )}
                              </div>
                              {contact.group_id && groupsById[contact.group_id] && (
                                <div className="text-[11px] text-muted-foreground truncate">{groupsById[contact.group_id].name}</div>
                              )}
                            </div>
                            {primaryPhone ? (
                              <a
                                href={`tel:${primaryPhone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="p-3 -mr-2 hover:bg-muted rounded"
                                title="Call"
                                data-testid={`contact-call-mobile-${contact.id}`}
                              >
                                <Phone className="w-5 h-5" />
                              </a>
                            ) : contact.email ? (
                              <a
                                href={`mailto:${contact.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="p-3 -mr-2 hover:bg-muted rounded"
                                title="Email"
                                data-testid={`contact-mail-mobile-${contact.id}`}
                              >
                                <Mail className="w-5 h-5" />
                              </a>
                            ) : null}
                          </div>
                        </div>

                        {/* INLINE EXPAND — same on desktop and mobile */}
                        {isExpanded && (
                          <div
                            className="px-3 md:px-12 pb-4 pt-1 bg-muted/20 border-t border-border/50"
                            data-testid={`contact-expanded-${contact.id}`}
                          >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                              {/* Phones + Emails */}
                              <div className="space-y-1.5">
                                <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Contact</h4>
                                {contact.relationship_status === 'warning' && (
                                  <div className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1.5">
                                    <span className="font-medium">Warning</span>
                                    {contact.relationship_status_reason && <span className="italic">— {contact.relationship_status_reason}</span>}
                                  </div>
                                )}
                                {contact.relationship_status === 'caution' && (
                                  <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">
                                    <span className="font-medium">Caution</span>
                                    {contact.relationship_status_reason && <span className="italic">— {contact.relationship_status_reason}</span>}
                                  </div>
                                )}
                                {phones.length === 0 && !contact.email && (
                                  <p className="text-xs text-muted-foreground/60">No phone or email</p>
                                )}
                                {phones.map((p, i) => (
                                  <a key={i} href={`tel:${p.value}`} className="flex items-center gap-2 text-xs hover:text-accent">
                                    <Phone className="w-3 h-3 shrink-0" />
                                    <span>{p.value}</span>
                                    <span className="text-[10px] text-muted-foreground">({p.label})</span>
                                  </a>
                                ))}
                                {contact.email && (
                                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-xs hover:text-accent break-all">
                                    <Mail className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{contact.email}</span>
                                  </a>
                                )}
                                {(contact.address_street || contact.address_city) && (
                                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                                    <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                                    <span>{[contact.address_street, contact.address_city, contact.address_state, contact.address_zip].filter(Boolean).join(', ')}</span>
                                  </div>
                                )}
                                {contact.trade && (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Briefcase className="w-3 h-3 shrink-0" />
                                    <span>{contact.trade}</span>
                                  </div>
                                )}
                              </div>

                              {/* Linked projects + last activity (lazy) */}
                              <div className="space-y-1.5">
                                <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Engagement</h4>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <FolderOpen className="w-3 h-3" />
                                  {details?.loading ? (
                                    <span>Loading…</span>
                                  ) : details?.loaded ? (
                                    <span><span className="font-medium text-foreground">{details.projects_count}</span> linked project{details.projects_count === 1 ? '' : 's'}</span>
                                  ) : <span>—</span>}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Activity className="w-3 h-3" />
                                  {details?.loading ? (
                                    <span>Loading…</span>
                                  ) : details?.loaded ? (
                                    lastActivity
                                      ? <span>Last activity <span className="text-foreground">{formatActivityDate(lastActivity)}</span></span>
                                      : <span>No activity yet</span>
                                  ) : <span>—</span>}
                                </div>
                                {contact.spec_categories?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 pt-1">
                                    {contact.spec_categories.slice(0, 6).map(prefix => (
                                      <Badge key={prefix} variant="outline" className="text-[10px] font-mono px-1 py-0 h-4">{prefix}</Badge>
                                    ))}
                                    {contact.spec_categories.length > 6 && (
                                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">+{contact.spec_categories.length - 6}</Badge>
                                    )}
                                  </div>
                                )}
                                {contact.tags?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 pt-1">
                                    {contact.tags.map(tag => <Badge key={tag} variant="secondary" className="text-[10px] px-1 py-0 h-4">{tag}</Badge>)}
                                  </div>
                                )}
                              </div>

                              {/* Actions column */}
                              <div className="flex flex-col gap-2 md:items-end">
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="w-full md:w-auto min-h-[44px] md:min-h-0"
                                  onClick={(e) => { e.stopPropagation(); setDetailContact(contact); setDetailOpen(true); }}
                                  data-testid={`open-drawer-${contact.id}`}
                                >
                                  <Maximize2 className="w-3.5 h-3.5 mr-1.5" />
                                  Open full details
                                </Button>
                                <div className="flex gap-1 md:justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); openEditDialog(contact); }}
                                    data-testid={`expanded-edit-${contact.id}`}
                                  >
                                    <Edit2 className="w-3.5 h-3.5 mr-1" />Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }}
                                    data-testid={`expanded-delete-${contact.id}`}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />Archive
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* ============ Add/Edit Dialog — extracted to ContactDialog (Feb 2026 refactor) ============ */}
      <ContactDialog
        form={form}
        specCategories={specCategories}
        CATEGORIES={CATEGORIES}
        catGroup={catGroup}
      />


      {/* ============ Merge Duplicates Dialog ============ */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5 text-amber-500" />Merge Duplicate Contacts</DialogTitle>
            <DialogDescription>
              {duplicateGroups.length > 0
                ? <>Found {duplicateGroups.length} group(s) of potential duplicates. Select the primary contact to keep.</>
                : 'No duplicate contacts found.'}
            </DialogDescription>
          </DialogHeader>
          {currentMergeGroup && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant={currentMergeGroup.confidence === 'high' ? 'default' : 'secondary'}>
                  {currentMergeGroup.confidence === 'high' ? 'High Confidence' : 'Medium Confidence'}
                </Badge>
                <span className="text-muted-foreground">Matched by {currentMergeGroup.match_type}: <strong>{currentMergeGroup.match_value}</strong></span>
              </div>
              <div className="space-y-3">
                {currentMergeGroup.contacts.map((contact, idx) => {
                  const isSel = selectedPrimary === contact.id;
                  const cName = contact.first_name ? `${contact.first_name} ${contact.last_name || ''}`.trim() : contact.name || 'Unknown';
                  return (
                    <div key={contact.id} onClick={() => setSelectedPrimary(contact.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${isSel ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'}`}
                      data-testid={`merge-contact-${idx}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{cName}</h4>
                            {isSel ? <Badge className="bg-primary text-primary-foreground"><Check className="w-3 h-3 mr-1" />Keep This</Badge>
                              : <Badge variant="outline" className="text-muted-foreground">Will be merged</Badge>}
                          </div>
                          {contact.company && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Building2 className="w-3 h-3" />{contact.company}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                        {contact.email && <div className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{contact.email}</div>}
                        {(contact.phone || contact.phone_mobile) && <div className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{contact.phone || contact.phone_mobile}</div>}
                        {contact.category && <div className="flex items-center gap-1"><Tag className="w-3 h-3" /><Badge variant="secondary" className="text-xs">{contact.category}</Badge></div>}
                        {contact.address_city && <div className="flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" />{contact.address_city}, {contact.address_state}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {duplicateGroups.length > 1 && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <span>Group {duplicateGroups.findIndex(g => g === currentMergeGroup) + 1} of {duplicateGroups.length}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setShowMergeDialog(false)} className="sm:mr-auto">Close</Button>
            {currentMergeGroup && duplicateGroups.length > 1 && (
              <Button variant="outline" onClick={skipMergeGroup}>Skip<ChevronRight className="w-4 h-4 ml-1" /></Button>
            )}
            {currentMergeGroup && (
              <Button onClick={handleMerge} disabled={merging} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-merge-btn">
                {merging ? 'Merging...' : <><GitMerge className="w-4 h-4 mr-2" />Merge {currentMergeGroup.contacts.length - 1} Contact(s)</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Bulk Change Category Dialog ============ */}
      <Dialog open={showBulkCategoryDialog} onOpenChange={setShowBulkCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FolderEdit className="w-5 h-5" />Change Category</DialogTitle>
            <DialogDescription>Update the category for {selectedContacts.size} selected contact(s).</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label>Choose an existing category</Label>
              <Select value={bulkCategory} onValueChange={(v) => { setBulkCategory(v); setBulkCategoryCustom(''); }}>
                <SelectTrigger className="mt-2" data-testid="bulk-category-select"><SelectValue placeholder="Choose a category..." /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 text-center">— or —</div>
            <div>
              <Label>Type a brand-new category name</Label>
              <Input
                value={bulkCategoryCustom}
                onChange={(e) => { setBulkCategoryCustom(e.target.value); if (e.target.value) setBulkCategory(''); }}
                placeholder="e.g. Preferred Vendor"
                className="mt-2"
                data-testid="bulk-category-custom-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkCategoryDialog(false); setBulkCategoryCustom(''); }}>Cancel</Button>
            <Button onClick={handleBulkCategoryChange} disabled={(!bulkCategory && !bulkCategoryCustom.trim()) || processingBulk} data-testid="confirm-bulk-category-btn">
              {processingBulk ? 'Updating...' : `Update ${selectedContacts.size} Contact(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Bulk Add Tag Dialog (2026-02) ============ */}
      <Dialog open={showBulkAddTagDialog} onOpenChange={setShowBulkAddTagDialog}>
        <DialogContent data-testid="bulk-add-tag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FolderEdit className="w-5 h-5" />Add Tag</DialogTitle>
            <DialogDescription>Add a tag to {selectedContacts.size} selected contact(s). Existing tags on those contacts are preserved; the new tag is appended without creating duplicates.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label>Pick an existing tag</Label>
              <Select value={bulkTagValue} onValueChange={(v) => { setBulkTagValue(v); setBulkTagCustom(''); }}>
                <SelectTrigger className="mt-2" data-testid="bulk-add-tag-select"><SelectValue placeholder="Choose a tag..." /></SelectTrigger>
                <SelectContent>{(allTags || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 text-center">— or —</div>
            <div>
              <Label>Type a brand-new tag</Label>
              <Input
                value={bulkTagCustom}
                onChange={(e) => { setBulkTagCustom(e.target.value); if (e.target.value) setBulkTagValue(''); }}
                placeholder="e.g. VIP"
                className="mt-2"
                data-testid="bulk-add-tag-custom-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAddTagDialog(false)}>Cancel</Button>
            <Button onClick={_doBulkAddTag} disabled={(!bulkTagValue && !bulkTagCustom.trim()) || processingBulk} data-testid="confirm-bulk-add-tag-btn">
              {processingBulk ? 'Adding…' : `Add tag to ${selectedContacts.size} contact${selectedContacts.size === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Bulk Remove Tag Dialog (2026-02) ============ */}
      <Dialog open={showBulkRemoveTagDialog} onOpenChange={setShowBulkRemoveTagDialog}>
        <DialogContent data-testid="bulk-remove-tag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FolderEdit className="w-5 h-5" />Remove Tag</DialogTitle>
            <DialogDescription>Remove a tag from {selectedContacts.size} selected contact(s). Only the chosen tag is stripped; every other field on the contact stays untouched, and the contacts are NOT deleted.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Pick a tag to remove</Label>
            <Select value={bulkTagValue} onValueChange={setBulkTagValue}>
              <SelectTrigger className="mt-2" data-testid="bulk-remove-tag-select"><SelectValue placeholder="Choose a tag..." /></SelectTrigger>
              <SelectContent>{(allTags || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkRemoveTagDialog(false)}>Cancel</Button>
            <Button onClick={_doBulkRemoveTag} disabled={!bulkTagValue || processingBulk} data-testid="confirm-bulk-remove-tag-btn">
              {processingBulk ? 'Removing…' : `Remove tag from ${selectedContacts.size} contact${selectedContacts.size === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Permanent Delete AlertDialog (2026-02-17, iter327) ============
          Lives ONLY in the Archive view. Requires typing the literal
          word "DELETE" before the destructive button enables. Backend
          POST /api/contacts/bulk/delete-permanent is tenant-scoped and
          rejects any id that isn't already archived. */}
      <Dialog
        open={permDeleteState.open}
        onOpenChange={(v) => { if (!v) setPermDeleteState({ open: false, ids: [], typed: '' }); }}
      >
        <DialogContent data-testid="perm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Permanently delete {permDeleteState.ids.length} contact{permDeleteState.ids.length === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription>
              This will hard-delete <span className="font-semibold">{permDeleteState.ids.length}</span> archived contact{permDeleteState.ids.length === 1 ? '' : 's'} from your tenant. <span className="font-semibold text-red-600">This action cannot be undone.</span> Foreign or non-archived ids are rejected server-side.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            <Label htmlFor="perm-delete-typed" className="text-sm">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm:
            </Label>
            <Input
              id="perm-delete-typed"
              value={permDeleteState.typed}
              onChange={(e) => setPermDeleteState(s => ({ ...s, typed: e.target.value }))}
              placeholder="DELETE"
              className="font-mono"
              data-testid="perm-delete-typed-input"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPermDeleteState({ open: false, ids: [], typed: '' })}
              data-testid="perm-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePermDeleteConfirm}
              disabled={permDeleteState.typed !== 'DELETE' || processingBulk}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="perm-delete-confirm-btn"
            >
              {processingBulk ? 'Deleting…' : `Permanently delete ${permDeleteState.ids.length} contact${permDeleteState.ids.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Contact Sync Settings (Feb 2026, iter 291) ============
          Provider-agnostic dialog — Google is live, others coming soon.
          Replaces the Google-specific import dialog. */}
      <ContactSyncSettings
        open={showGoogleImport}
        onOpenChange={setShowGoogleImport}
      />

      {/* ============ Add to RFP Dialog (Feature B) ============ */}
      <Dialog open={showRfpDialog} onOpenChange={setShowRfpDialog}>
        <DialogContent className="max-w-md" data-testid="rfp-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-primary" />
              Add {selectedContacts.size} contact{selectedContacts.size === 1 ? '' : 's'} to RFP
            </DialogTitle>
            <DialogDescription>
              RFPs are scoped to a project. Pick a project, then choose an existing draft RFP or create a new one for this trade.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={rfpProjectId} onValueChange={setRfpProjectId}>
                <SelectTrigger data-testid="rfp-project-select"><SelectValue placeholder="Pick a project…" /></SelectTrigger>
                <SelectContent>
                  {rfpProjects.length === 0 && (
                    <div className="px-2 py-3 text-xs text-muted-foreground">No projects</div>
                  )}
                  {rfpProjects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {rfpProjectId && (
              <div className="space-y-2">
                <Label>RFP</Label>
                <Select value={rfpTargetId} onValueChange={setRfpTargetId}>
                  <SelectTrigger data-testid="rfp-target-select"><SelectValue placeholder="Pick or create…" /></SelectTrigger>
                  <SelectContent>
                    {rfpDraftList.map(rfp => (
                      <SelectItem key={rfp.id} value={rfp.id}>
                        {rfp.rfp_number || 'RFP'} · {rfp.title || rfp.trade || 'Draft'} ({(rfp.contractors || []).length} recipient{(rfp.contractors || []).length === 1 ? '' : 's'})
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">
                      <span className="flex items-center gap-1"><Plus className="w-3 h-3" /> Create new RFP{selectedCategory !== 'all' ? ` for ${selectedCategory}` : ''}</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {rfpDraftList.length === 0 && (
                  <p className="text-xs text-muted-foreground">No draft RFPs on this project — a new one will be created.</p>
                )}
              </div>
            )}

            {rfpTargetId === '__new__' && (
              <div className="space-y-2">
                <Label>RFP Title</Label>
                <Input
                  value={rfpNewTitle}
                  onChange={(e) => setRfpNewTitle(e.target.value)}
                  placeholder={selectedCategory !== 'all' ? `RFP for ${selectedCategory}` : 'RFP'}
                  data-testid="rfp-new-title-input"
                />
                <p className="text-xs text-muted-foreground">
                  The new RFP will be created as a Draft. You can finish scope, due date, and send from the RFPs page.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRfpDialog(false)} disabled={rfpSubmitting}>Cancel</Button>
            <Button
              onClick={submitAddToRfp}
              disabled={rfpSubmitting || !rfpProjectId || !rfpTargetId}
              data-testid="rfp-submit-btn"
            >
              {rfpSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</>) :
                rfpTargetId === '__new__' ? 'Create RFP & add' : 'Add to RFP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Warning Gate (Feature A guardrail) ============ */}
      <WarningGateDialog
        open={warningGate.open}
        onOpenChange={(o) => setWarningGate(g => ({ ...g, open: o }))}
        actionLabel={warningGate.label}
        warningContacts={warningGate.contacts}
        onConfirm={warningGate.action || (() => {})}
        loading={processingBulk || rfpSubmitting}
      />

      {/* Phase 3 — Contact Hub detail drawer */}
      <ContactDetailDrawer
        contact={detailContact}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
}
